/**
 * DV-TEMP-004: WebSocket Cache Invalidation Patterns
 *
 * Centralized React Query key builders and invalidation strategies for WebSocket events.
 * Prevents key drift and documents exact invalidation behavior per event type.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { EventType } from '@/types/events-v2';
import type { StageName } from '@/lib/shared-types';
import { timelineKeys } from '@/types/execution-events';

// ============================================================================
// CENTRALIZED QUERY KEY BUILDERS
// ============================================================================

/**
 * All React Query keys used by temporal demo viewer.
 * IMPORTANT: These are the single source of truth for all cache keys.
 * Any new keys must be added here to prevent drift.
 */
export const queryKeys = {
  // Execution-level keys
  execution: {
    base: (executionId: string) => ['execution', executionId] as const,
    status: (executionId: string) => ['execution', executionId, 'status'] as const,
    settings: (executionId: string) => ['execution', executionId, 'settings'] as const,
    events: (executionId: string) => ['execution', executionId, 'events'] as const, // Legacy
    qcSummary: (executionId: string) => ['execution', executionId, 'qc-summary'] as const,
    thresholds: (executionId: string) => ['execution', executionId, 'thresholds'] as const,
  },

  // Timeline keys (from types/execution-events.ts)
  timeline: {
    all: () => timelineKeys.all,
    execution: (executionId: string) => timelineKeys.execution(executionId),
    filtered: (executionId: string, filters?: any) => ['timeline', executionId, 'filtered', filters] as const,
  },

  // Stage-level keys
  stage: {
    base: (executionId: string, stage: StageName) => ['execution', executionId, 'stage', stage] as const,
    settings: (executionId: string, stage: StageName) => ['execution', executionId, 'stage', stage, 'settings'] as const,
    iterations: (executionId: string, stage: StageName) => ['execution', executionId, 'stage', stage, 'iterations'] as const,
    outputs: (executionId: string, stage: StageName) => ['execution', executionId, 'stage', stage, 'outputs'] as const,
    attempts: (executionId: string, stage: StageName) => ['execution', executionId, 'stage', stage, 'attempts'] as const,
  },

  // Temporal workflow keys
  temporal: {
    workflowId: (workflowId: string) => ['temporal', 'workflow', workflowId] as const,
    workflowRun: (workflowId: string, runId: string) => ['temporal', 'workflow', workflowId, 'run', runId] as const,
    workflowHistory: (workflowId: string, runId: string) => ['temporal', 'workflow', workflowId, 'run', runId, 'history'] as const,
  },

  // Execution list keys
  executions: {
    list: () => ['executions'] as const,
    recent: () => ['executions', 'recent'] as const,
    byProject: (projectId: string) => ['executions', 'project', projectId] as const,
  }
} as const;

// ============================================================================
// EVENT-SPECIFIC INVALIDATION STRATEGIES
// ============================================================================

/**
 * Defines which React Query keys to invalidate for each WebSocket event type.
 * This is the authoritative mapping that prevents cache inconsistencies.
 */
export const invalidationStrategy: Record<EventType, {
  keys: (executionId: string, data?: any) => any[];
  description: string;
  immediate: boolean; // Whether to invalidate immediately or debounce
}> = {
  // ========================================
  // Pipeline Lifecycle Events
  // ========================================

  pipeline_started: {
    keys: (executionId: string) => [
      queryKeys.execution.base(executionId),
      queryKeys.execution.status(executionId),
      queryKeys.timeline.execution(executionId),
      queryKeys.executions.list(),
      queryKeys.executions.recent()
    ],
    description: 'Pipeline start: invalidate execution status and timeline',
    immediate: true
  },

  pipeline_completed: {
    keys: (executionId: string) => [
      queryKeys.execution.base(executionId),
      queryKeys.execution.status(executionId),
      queryKeys.timeline.execution(executionId),
      queryKeys.execution.qcSummary(executionId),
      queryKeys.executions.list(),
      queryKeys.executions.recent()
    ],
    description: 'Pipeline completion: invalidate execution, timeline, and QC summary',
    immediate: true
  },

  // ========================================
  // Stage Lifecycle Events
  // ========================================

  stage_started: {
    keys: (executionId: string, data?: any) => {
      const stage = data?.stage?.stage || data?.stage?.name || data?.stage;
      const baseKeys: any[] = [
        queryKeys.execution.status(executionId),
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId)
      ];

      if (stage) {
        baseKeys.push(
          queryKeys.stage.base(executionId, stage),
          queryKeys.stage.settings(executionId, stage),
          queryKeys.stage.attempts(executionId, stage)
        );
      }

      return baseKeys;
    },
    description: 'Stage start: invalidate execution status, timeline, and specific stage data',
    immediate: true
  },

  stage_completed: {
    keys: (executionId: string, data?: any) => {
      const stage = data?.stage?.stage || data?.stage?.name || data?.stage;
      const baseKeys: any[] = [
        queryKeys.execution.status(executionId),
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId),
        queryKeys.execution.qcSummary(executionId)
      ];

      if (stage) {
        baseKeys.push(
          queryKeys.stage.base(executionId, stage),
          queryKeys.stage.iterations(executionId, stage),
          queryKeys.stage.outputs(executionId, stage),
          queryKeys.stage.attempts(executionId, stage)
        );
      }

      return baseKeys;
    },
    description: 'Stage completion: invalidate execution, timeline, stage data, and QC summary',
    immediate: true
  },

  stage_failed: {
    keys: (executionId: string, data?: any) => {
      const stage = data?.stage?.stage || data?.stage?.name || data?.stage;
      const baseKeys: any[] = [
        queryKeys.execution.status(executionId),
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId)
      ];

      if (stage) {
        baseKeys.push(
          queryKeys.stage.base(executionId, stage),
          queryKeys.stage.attempts(executionId, stage)
        );
      }

      return baseKeys;
    },
    description: 'Stage failure: invalidate execution status, timeline, and failed stage data',
    immediate: true
  },

  // ========================================
  // Content and Output Events
  // ========================================

  content_ready: {
    keys: (executionId: string, data?: any) => {
      const stage = data?.stage?.stage || data?.stage?.name || data?.stage;
      const baseKeys: any[] = [
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId)
      ];

      if (stage) {
        baseKeys.push(
          queryKeys.stage.outputs(executionId, stage),
          queryKeys.stage.iterations(executionId, stage)
        );
      }

      return baseKeys;
    },
    description: 'Content ready: invalidate timeline and stage outputs',
    immediate: false // Can be debounced since it\'s informational
  },

  // ========================================
  // Quality Control Events
  // ========================================

  qc_started: {
    keys: (executionId: string, data?: any) => {
      const targetStage = data?.payload?.target_stage || data?.stage;
      const baseKeys: any[] = [
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId)
      ];

      if (targetStage) {
        baseKeys.push(queryKeys.stage.base(executionId, targetStage));
      }

      return baseKeys;
    },
    description: 'QC start: invalidate timeline and target stage',
    immediate: false
  },

  qc_completed: {
    keys: (executionId: string, data?: any) => {
      const targetStage = data?.payload?.target_stage || data?.stage;
      const baseKeys: any[] = [
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId),
        queryKeys.execution.qcSummary(executionId)
      ];

      if (targetStage) {
        baseKeys.push(queryKeys.stage.base(executionId, targetStage));
      }

      return baseKeys;
    },
    description: 'QC completion: invalidate timeline, QC summary, and target stage',
    immediate: true
  },

  qc_failed: {
    keys: (executionId: string, data?: any) => {
      const targetStage = data?.payload?.target_stage || data?.stage;
      const baseKeys: any[] = [
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId),
        queryKeys.execution.qcSummary(executionId)
      ];

      if (targetStage) {
        baseKeys.push(queryKeys.stage.base(executionId, targetStage));
      }

      return baseKeys;
    },
    description: 'QC failure: invalidate timeline, QC summary, and target stage',
    immediate: true
  },

  scores_ready: {
    keys: (executionId: string, data?: any) => {
      const targetStage = data?.payload?.target_stage || data?.stage;
      const baseKeys: any[] = [
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId),
        queryKeys.execution.qcSummary(executionId)
      ];

      if (targetStage) {
        baseKeys.push(queryKeys.stage.base(executionId, targetStage));
      }

      return baseKeys;
    },
    description: 'QC scores ready: invalidate timeline, QC summary, and target stage',
    immediate: true
  },

  quality_warning: {
    keys: (executionId: string) => [
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId),
      queryKeys.execution.qcSummary(executionId)
    ],
    description: 'Quality warning: invalidate timeline and QC summary',
    immediate: true
  },

  // ========================================
  // Billing Events
  // ========================================

  billing_committed: {
    keys: (executionId: string) => [
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId),
      queryKeys.execution.status(executionId)
    ],
    description: 'Billing committed: invalidate timeline and execution status',
    immediate: false // Can be debounced
  },

  billing_record_created: {
    keys: (executionId: string) => [
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId)
    ],
    description: 'Billing record created: invalidate timeline',
    immediate: false // Can be debounced
  },

  // ========================================
  // Export and Processing Events
  // ========================================

  export_preview: {
    keys: (executionId: string) => [
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId),
      queryKeys.execution.status(executionId)
    ],
    description: 'Export preview ready: invalidate timeline and execution status',
    immediate: true
  },

  processing: {
    keys: (executionId: string) => [
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId)
    ],
    description: 'Processing event: invalidate timeline',
    immediate: false // Can be debounced for performance
  },

  // ========================================
  // Error Events
  // ========================================

  error: {
    keys: (executionId: string, data?: any) => {
      const stage = data?.stage?.stage || data?.stage?.name || data?.stage;
      const baseKeys: any[] = [
        queryKeys.execution.status(executionId),
        queryKeys.timeline.execution(executionId),
        queryKeys.timeline.filtered(executionId)
      ];

      if (stage) {
        baseKeys.push(
          queryKeys.stage.base(executionId, stage),
          queryKeys.stage.attempts(executionId, stage)
        );
      }

      return baseKeys;
    },
    description: 'Error event: invalidate execution status, timeline, and relevant stage',
    immediate: true
  }
};

// ============================================================================
// WEBSOCKET EVENT HANDLERS
// ============================================================================

/**
 * Mapping of WebSocket event types to the data events they should trigger invalidation for.
 * This handles the translation between WebSocket events and data invalidation.
 */
export const webSocketEventMapping = {
  // Timeline events
  'timeline:event_added': (data: any) => ({
    eventType: data.event?.event_type as EventType,
    executionId: data.executionId,
    eventData: data.event
  }),

  'timeline:events_batch': (data: any) => ({
    eventType: 'batch_update' as const, // Special batch handling
    executionId: data.executionId,
    eventData: { events: data.events }
  }),

  'timeline:event_updated': (data: any) => ({
    eventType: 'event_update' as const, // Special update handling
    executionId: data.executionId,
    eventData: { eventId: data.eventId, updates: data.updates }
  }),

  // Execution events
  'execution:progress': (data: any) => ({
    eventType: 'processing' as EventType,
    executionId: data.executionId,
    eventData: data
  }),

  'execution:stage_completed': (data: any) => ({
    eventType: 'stage_completed' as EventType,
    executionId: data.executionId,
    eventData: data
  }),

  'execution:failed': (data: any) => ({
    eventType: 'error' as EventType,
    executionId: data.executionId,
    eventData: data
  })
} as const;

// ============================================================================
// INVALIDATION EXECUTOR
// ============================================================================

/**
 * Interface for WebSocket invalidation manager
 */
export interface WebSocketInvalidationManager {
  invalidateForEvent(eventType: EventType | 'batch_update' | 'event_update', executionId: string, data?: any): void;
  handleWebSocketEvent(eventType: keyof typeof webSocketEventMapping, rawData: any): void;
  forceInvalidateExecution(executionId: string): void;
  destroy(): void;
}

/**
 * Central cache invalidation handler for WebSocket events.
 * This is the single function that should be called for all WebSocket-triggered invalidations.
 */
export class WebSocketInvalidationManagerImpl implements WebSocketInvalidationManager {
  private queryClient: QueryClient;
  private debounceMap = new Map<string, NodeJS.Timeout>();
  private readonly DEBOUNCE_MS = 100; // Debounce non-immediate invalidations

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  /**
   * Handle invalidation for a specific event type and execution
   */
  invalidateForEvent(eventType: EventType | 'batch_update' | 'event_update', executionId: string, data?: any) {
    // Handle special cases
    if (eventType === 'batch_update') {
      this.handleBatchUpdate(executionId, data);
      return;
    }

    if (eventType === 'event_update') {
      this.handleEventUpdate(executionId, data);
      return;
    }

    // Get invalidation strategy for this event type
    const strategy = invalidationStrategy[eventType];
    if (!strategy) {
      console.warn(`No invalidation strategy found for event type: ${eventType}`);
      return;
    }

    // Get keys to invalidate
    const keysToInvalidate = strategy.keys(executionId, data);

    // Log invalidation for debugging
    if (import.meta.env.DEV) {
      console.log(`🔄 WebSocket invalidation for ${eventType}:`, {
        executionId,
        strategy: strategy.description,
        immediate: strategy.immediate,
        keys: keysToInvalidate.map(key => key.join('/')),
        data: data ? Object.keys(data) : []
      });
    }

    // Execute invalidation (immediate or debounced)
    if (strategy.immediate) {
      this.executeInvalidation(keysToInvalidate);
    } else {
      this.debounceInvalidation(`${eventType}:${executionId}`, keysToInvalidate);
    }
  }

  /**
   * Handle WebSocket events using the mapping
   */
  handleWebSocketEvent(eventType: keyof typeof webSocketEventMapping, rawData: any) {
    const mapper = webSocketEventMapping[eventType];
    if (!mapper) {
      console.warn(`No WebSocket event mapping found for: ${eventType}`);
      return;
    }

    const { eventType: dataEventType, executionId, eventData } = mapper(rawData);
    this.invalidateForEvent(dataEventType, executionId, eventData);
  }

  /**
   * Handle batch updates (multiple events at once)
   */
  private handleBatchUpdate(executionId: string, _data?: any) {
    // For batch updates, invalidate broad categories
    const keysToInvalidate = [
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId),
      queryKeys.execution.status(executionId)
    ];

    this.executeInvalidation(keysToInvalidate);
  }

  /**
   * Handle single event updates
   */
  private handleEventUpdate(executionId: string, _data?: any) {
    // For event updates, just invalidate timeline
    const keysToInvalidate = [
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId)
    ];

    this.executeInvalidation(keysToInvalidate);
  }

  /**
   * Execute immediate invalidation
   */
  private executeInvalidation(keys: any[]) {
    keys.forEach(queryKey => {
      this.queryClient.invalidateQueries({ queryKey });
    });
  }

  /**
   * Debounce invalidation for performance
   */
  private debounceInvalidation(key: string, keys: any[]) {
    // Clear existing timeout
    const existingTimeout = this.debounceMap.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(() => {
      this.executeInvalidation(keys);
      this.debounceMap.delete(key);
    }, this.DEBOUNCE_MS);

    this.debounceMap.set(key, timeout);
  }

  /**
   * Force invalidation of all keys for an execution (emergency refresh)
   */
  forceInvalidateExecution(executionId: string) {
    const allKeys: any[] = [
      queryKeys.execution.base(executionId),
      queryKeys.execution.status(executionId),
      queryKeys.execution.settings(executionId),
      queryKeys.execution.events(executionId),
      queryKeys.execution.qcSummary(executionId),
      queryKeys.execution.thresholds(executionId),
      queryKeys.timeline.execution(executionId),
      queryKeys.timeline.filtered(executionId)
    ];

    // Also invalidate stage keys (all possible stages)
    const stages: StageName[] = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'frames', 'videos', 'elements', 'music', 'qc', 'assemble', 'publish', 'music_master', 'music_sidechain', 'export_preview'];
    stages.forEach(stage => {
      allKeys.push(
        queryKeys.stage.base(executionId, stage),
        queryKeys.stage.settings(executionId, stage),
        queryKeys.stage.iterations(executionId, stage),
        queryKeys.stage.outputs(executionId, stage),
        queryKeys.stage.attempts(executionId, stage)
      );
    });

    this.executeInvalidation(allKeys);

    console.log('🔄 Force invalidated all keys for execution:', executionId);
  }

  /**
   * Clean up debounce timers
   */
  destroy() {
    this.debounceMap.forEach(timeout => clearTimeout(timeout));
    this.debounceMap.clear();
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Create a WebSocket invalidation manager instance
 */
export function createInvalidationManager(queryClient: QueryClient): WebSocketInvalidationManager {
  return new WebSocketInvalidationManagerImpl(queryClient);
}

/**
 * Get all possible query keys for an execution (for debugging)
 */
export function getAllExecutionKeys(executionId: string) {
  const keys: any[] = [
    ...Object.values(queryKeys.execution).map(fn => fn(executionId)),
  ];

  // Handle timeline keys specifically
  keys.push(queryKeys.timeline.all());
  keys.push(queryKeys.timeline.execution(executionId));
  keys.push(queryKeys.timeline.filtered(executionId, {}));

  // Add stage keys for all possible stages
  const stages: StageName[] = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'frames', 'videos', 'elements', 'music', 'qc', 'assemble', 'publish', 'music_master', 'music_sidechain', 'export_preview'];
  stages.forEach(stage => {
    keys.push(queryKeys.stage.base(executionId, stage));
    keys.push(queryKeys.stage.settings(executionId, stage));
    keys.push(queryKeys.stage.iterations(executionId, stage));
    keys.push(queryKeys.stage.outputs(executionId, stage));
    keys.push(queryKeys.stage.attempts(executionId, stage));
  });

  return keys.filter(Boolean);
}

/**
 * Validate that all event types have invalidation strategies
 */
export function validateInvalidationCoverage() {
  // This would be used in tests to ensure we have coverage for all event types
  // Implementation would check against the EventType union type
}

// Export types for external usage
export type WebSocketEventType = keyof typeof webSocketEventMapping;
export type InvalidationStrategy = typeof invalidationStrategy[EventType];